import React from 'react';
import './CarCard.css';

export interface Car {
  id: string;
  title: string;
  year: number;
  make: string;
  model: string;
  mileage: string;
  price: string;
  description: string;
  features: string[];
  issues?: string[];
  images: string[];
  photoLink?: string;
  status: 'available' | 'sold';
  datePosted: string;
}

interface CarCardProps {
  car: Car;
}

const CarCard: React.FC<CarCardProps> = ({ car }) => {
  const [imgError, setImgError] = React.useState(false);
  const firstImage = car.images[0];
  React.useEffect(() => { setImgError(false); }, [firstImage]);

  const handleContactClick = () => {
    const subject = `Inquiry about ${car.title}`;
    const body = `Hi, I'm interested in the ${car.title}. Could you please provide more information?`;
    const mailtoLink = `mailto:eurojdmauto@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const handlePhotoClick = () => {
    if (car.photoLink) {
      window.open(car.photoLink, '_blank');
    }
  };

  return (
    <div className={`car-card ${car.status === 'sold' ? 'car-card-sold' : ''}`}>
      {car.status === 'sold' && (
        <div className="sold-badge">Sold</div>
      )}

      <div className="car-image-wrapper">
        {car.images.length > 0 && !imgError ? (
          <img
            src={car.images[0]}
            alt={car.title}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="car-no-image">
            <div className="no-image-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <span>No Image Available</span>
          </div>
        )}
        {car.photoLink && (
          <button className="photo-link-btn" onClick={handlePhotoClick}>
            View Photos &rarr;
          </button>
        )}
        <div className="car-year-badge">{car.year}</div>
      </div>

      <div className="car-body">
        <div className="car-header">
          <h3 className="car-title">{car.title}</h3>
          <span className="car-mileage">{car.mileage}</span>
        </div>

        <p className="car-description">{car.description}</p>

        {car.features.length > 0 && (
          <div className="car-features">
            <span className="features-label">Key Features</span>
            <div className="features-list">
              {car.features.map((feature, index) => (
                <span key={index} className="feature-tag">{feature}</span>
              ))}
            </div>
          </div>
        )}

        {car.issues && car.issues.length > 0 && (
          <div className="car-issues">
            <span className="issues-label">Notes</span>
            <div className="issues-list">
              {car.issues.map((issue, index) => (
                <span key={index} className="issue-tag">{issue}</span>
              ))}
            </div>
          </div>
        )}

        <div className="car-footer">
          <div className="car-price">
            <span className="price-label">Price</span>
            <span className="price-value">{car.price}</span>
          </div>
          <button
            className="car-cta"
            onClick={handleContactClick}
            disabled={car.status === 'sold'}
          >
            {car.status === 'sold' ? 'Sold' : 'Inquire Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarCard;
